import React, { useState, useEffect } from 'react';
import {
    Container, Title, Text, TextInput, Textarea, Button, Grid,
    createStyles, Paper, Group, FileInput, LoadingOverlay, Notification, Stack, ThemeIcon
} from '@mantine/core';
import { IconUpload, IconSend, IconCheck, IconX, Icon3dCubeSphere, IconBuildingFactory, IconRocket } from '@tabler/icons-react';
import axios from 'axios';

const useStyles = createStyles((theme) => ({
    wrapper: {
        paddingTop: 80,
        paddingBottom: 80,
        minHeight: '100vh',
        backgroundColor: '#050505',
        backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
        overflowX: 'hidden',
    },

    title: {
        color: theme.white,
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        fontSize: 44,
        lineHeight: 1.2,
        fontWeight: 900,
        marginBottom: theme.spacing.xl,
        textAlign: 'center',
        [theme.fn.smallerThan('sm')]: {
            fontSize: 32,
        },
    },

    subtitle: {
        color: theme.colors.gray[4],
        textAlign: 'center',
        marginBottom: theme.spacing.xl * 2,
        maxWidth: 700,
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '40px'
    },

    glassPanel: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        padding: theme.spacing.xl * 1.5,
        borderRadius: theme.radius.lg,
        height: '100%',
        color: theme.white,
        position: 'relative',
        padding: '20px'
    },

    input: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        color: theme.white,
        '&:focus': {
            borderColor: theme.colors.cyan[6],
        },
        '& input, & textarea': {
            color: theme.white,
        },
        '& input::placeholder, & textarea::placeholder': {
            color: theme.colors.gray[5],
        }
    },

    label: {
        color: theme.colors.gray[3],
    },

    feature: {
        padding: theme.spacing.lg,
        borderRadius: theme.radius.md,
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'transform 200ms ease',
        '&:hover': {
            transform: 'translateY(-5px)',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
        }
    }
}));

export default function InquiryForm() {
    const { classes } = useStyles();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [file, setFile] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        companyName: '',
        description: '',
        fileUrl: ''
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (file) => {
        setFile(file);
        if (file) {
            setUploading(true);
            const uploadData = new FormData();
            uploadData.append('modelFile', file); // Matching backend 'modelFile' field

            try {
                const response = await axios.post('https://backend.cdp3d.com/api/upload-model', uploadData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setFormData(prev => ({ ...prev, fileUrl: response.data.filePath }));
            } catch (err) {
                console.error("Upload failed", err);
                setError("File upload failed. Please try again.");
            } finally {
                setUploading(false);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (uploading) {
            setError("Please wait for the file to finish uploading.");
            setLoading(false);
            return;
        }

        try {
            await axios.post('https://backend.cdp3d.com/enquiry', formData);
            setSuccess(true);
            setFormData({ name: '', email: '', phone: '', companyName: '', description: '', fileUrl: '' });
            setFile(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={classes.wrapper}>
            <Container size="xl">
                <Title className={classes.title}>Custom Manufacturing Enquiry</Title>
                <Text className={classes.subtitle}>
                    Need a custom 3D printed part, prototype, or batch production?
                    Share your requirements with us, and we'll provide a technical consultation and quote.
                </Text>

                <Grid gutter="xl">
                    <Grid.Col md={5}>
                        <Stack spacing="xl">
                            <Paper className={classes.feature}>
                                <ThemeIcon size={50} radius="md" variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }} mb="md">
                                    <Icon3dCubeSphere size={30} />
                                </ThemeIcon>
                                <Title order={4} mb="xs" color="white">Rapid Prototyping</Title>
                                <Text color="dimmed" size="sm">
                                    Turn your ideas into physical models within days. High-precision SLA and FDM printing available.
                                </Text>
                            </Paper>

                            <Paper className={classes.feature}>
                                <ThemeIcon size={50} radius="md" variant="gradient" gradient={{ from: 'teal', to: 'lime' }} mb="md">
                                    <IconBuildingFactory size={30} />
                                </ThemeIcon>
                                <Title order={4} mb="xs" color="white">Batch Production</Title>
                                <Text color="dimmed" size="sm">
                                    Scalable manufacturing solutions for small to medium batch runs with consistent quality.
                                </Text>
                            </Paper>

                            <Paper className={classes.feature}>
                                <ThemeIcon size={50} radius="md" variant="gradient" gradient={{ from: 'orange', to: 'red' }} mb="md">
                                    <IconRocket size={30} />
                                </ThemeIcon>
                                <Title order={4} mb="xs" color="white">Industrial Grade Materials</Title>
                                <Text color="dimmed" size="sm">
                                    Choose from excessive range of materials including PLA, PETG, ABS, Resin, and Carbon Fiber composites.
                                </Text>
                            </Paper>
                        </Stack>
                    </Grid.Col>

                    <Grid.Col md={7}>
                        <Paper className={classes.glassPanel}>
                            <LoadingOverlay visible={loading} overlayBlur={2} />
                            <Title order={3} mb="lg" color="white" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                Project Details
                            </Title>

                            <form onSubmit={handleSubmit}>
                                <Stack spacing="md">
                                    <Grid gutter="md">
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label="Name"
                                                placeholder="Your name"
                                                required
                                                classNames={{ input: classes.input, label: classes.label }}
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label="Company (Optional)"
                                                placeholder="Your company"
                                                classNames={{ input: classes.input, label: classes.label }}
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                            />
                                        </Grid.Col>
                                    </Grid>

                                    <Grid gutter="md">
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label="Email"
                                                placeholder="your@email.com"
                                                required
                                                classNames={{ input: classes.input, label: classes.label }}
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label="Phone"
                                                placeholder="Your phone"
                                                required
                                                classNames={{ input: classes.input, label: classes.label }}
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </Grid.Col>
                                    </Grid>

                                    <Textarea
                                        label="Project Description"
                                        placeholder="Describe your requirements, dimensions, material preference, and quantity..."
                                        minRows={5}
                                        required
                                        classNames={{ input: classes.input, label: classes.label }}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                    />

                                    <FileInput
                                        label="Upload 3D Model / Reference (Optional)"
                                        placeholder={uploading ? "Uploading..." : "Click to select file"}
                                        icon={<IconUpload size={18} />}
                                        classNames={{ input: classes.input, label: classes.label }}
                                        value={file}
                                        onChange={handleFileChange}
                                        disabled={uploading}
                                    />
                                    {formData.fileUrl && (
                                        <Text size="xs" color="teal">File uploaded successfully!</Text>
                                    )}

                                    {success && (
                                        <Notification icon={<IconCheck size={18} />} color="teal" title="Enquiry Sent" onClose={() => setSuccess(false)}>
                                            Thank you! We have received your enquiry and will contact you shortly.
                                        </Notification>
                                    )}

                                    {error && (
                                        <Notification icon={<IconX size={18} />} color="red" title="Error" onClose={() => setError(null)}>
                                            {error}
                                        </Notification>
                                    )}

                                    <Button
                                        type="submit"
                                        size="lg"
                                        fullWidth
                                        variant="gradient"
                                        gradient={{ from: 'teal', to: 'green' }}
                                        rightIcon={<IconSend size={18} />}
                                        loading={uploading}
                                    >
                                        Submit Enquiry
                                    </Button>
                                </Stack>
                            </form>
                        </Paper>
                    </Grid.Col>
                </Grid>
            </Container>
        </div>
    );
}